import { useEffect, useRef, useState } from 'react';

import type { ProfileDocumentType } from '@xing-com/crate-common-graphql-types';

import { useFileServerUpload } from '../use-file-server-upload';

export type UploadedFile = {
  id?: string | null;
  profileDocumentId?: string | null;
  stagedUploadId?: string | null;
  authToken?: string | null;
  url?: string | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  progress: number;
  onCancel: (msg?: string) => void;
};

type Props = {
  onUploadStatusUpdate: (isUploading: boolean) => void;
  profileId?: string;
  documentType: ProfileDocumentType;
};

export type UseUploadResult = {
  handleDeleteUpload: (upload: UploadedFile) => Promise<void>;
  handleUploadFile: (file: File) => Promise<void>;
  uploads: UploadedFile[];
};

export const useUpload = ({
  onUploadStatusUpdate,
  profileId,
  documentType,
}: Props): UseUploadResult => {
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const { deleteFile, deleteProfileDocument, uploadFile } =
    useFileServerUpload();
  const isMountedRef = useRef<boolean>(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDeleteUpload = async ({
    id,
    profileDocumentId,
    stagedUploadId,
    authToken,
    progress,
    onCancel,
  }: UploadedFile): Promise<void> => {
    if (progress !== 100) onCancel();

    if (profileDocumentId && profileId) {
      await deleteProfileDocument({
        profileId,
        documentId: profileDocumentId,
      });
      setUploads((prevUploads) =>
        prevUploads.filter((upload) => id !== upload.id)
      );
    }

    await deleteFile({ id: stagedUploadId ?? id, authToken });

    return setUploads((prevUploads) =>
      prevUploads.filter((upload) => id !== upload.id)
    );
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const handleUploadFile = async (file: any): Promise<void> => {
    const abortController = new AbortController();
    let requestedUploadId: UploadedFile['id'];

    try {
      const {
        upload: { id },
        status,
        profileDocumentId,
      } = await uploadFile(file, {
        signal: abortController.signal,
        profileDocument: profileId ? { profileId, documentType } : undefined,
        onUploadRequested: ({ id, authToken, url }) => {
          requestedUploadId = id;
          const onCancel = (): void => {
            abortController.abort();
            onUploadStatusUpdate(
              uploads.some(({ progress }) => progress !== 100)
            );
          };

          setUploads((prevUploads) => [
            ...prevUploads,
            {
              id,
              stagedUploadId: id,
              authToken,
              url,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              onCancel,
              progress: 0,
            },
          ]);

          onUploadStatusUpdate(true);
        },
        onUploadProgress:
          // FIXME: This is a workaround because msw doesn't properly mock the
          // request object and without this it fails with:
          // "request.upload.addEventListener in not a function"
          typeof jest !== 'undefined'
            ? undefined
            : (progress, { id }) => {
                const isProfileDocumentCreationPending =
                  progress === 100 && Boolean(profileId);
                const displayedProgress = isProfileDocumentCreationPending
                  ? 99
                  : progress;
                setUploads((prevUploads) =>
                  prevUploads.map((upload) =>
                    upload.id === id
                      ? { ...upload, progress: displayedProgress }
                      : upload
                  )
                );

                onUploadStatusUpdate(
                  progress !== 100 || isProfileDocumentCreationPending
                );
              },
      });

      if (status === 204) {
        onUploadStatusUpdate(uploads.some(({ progress }) => progress !== 100));
        if (isMountedRef.current) {
          setUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.id === id
                ? {
                    ...upload,
                    id: profileDocumentId ?? id,
                    profileDocumentId,
                    progress: 100,
                  }
                : upload
            )
          );
        }
        return;
      }
    } catch (error) {
      if (requestedUploadId !== undefined) {
        setUploads(uploads.filter((upload) => requestedUploadId !== upload.id));
      }
      throw error;
    }

    throw new Error('Error uploading file');
  };

  return { handleUploadFile, handleDeleteUpload, uploads };
};
